import {Api} from '@/ts/api';
import {useRouter} from 'vue-router';
import {pagesPath} from '@/ts/router/pages-path';
import {useLang} from '@/ts/composables/stateful/use-lang';
import {
  LOGOUT_ON_401,
  ZITADEL,
} from '@/ts/utils/consts';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import type {ZitadelGetTokenResponse} from '@/ts/types/dto/zitadel-dto';
import {useLogger} from '@/ts/composables/pure/use-logger';
import type {LogMethods} from '@significo/pulse';
import {useUserStore} from '@/ts/store/user-store';
import {Notify} from 'quasar';
import {useI18n} from 'vue-i18n';
import {endLocalSession} from '@/ts/composables/stateful/use-auth';
import {unauthenticatedRedirectOf} from '@/ts/router/unauthenticated-redirect';
import {AppErrorCode} from '@/ts/types/dto/error-codes-dto';

let tokenLock: Promise<void> | null = null;

async function refreshToken(logger: LogMethods, api: Api): Promise<void> {
  if (tokenLock) {
    logger.debug('Token is expired, waiting for other process to refresh it')();
    await tokenLock;
    logger.debug('Refresh token is done, continuing')();
  } else {
    logger.info('Token has been expired. Refreshing it and acquire the lock', persistentStorage.auth)();
    let resolve: () => void = null!;
    tokenLock = new Promise(res => {
      resolve = res;
    });
    try {
      const response = await api.zitadel.refreshToken(
        persistentStorage.auth!.refresh_token,
        ZITADEL,
      );
      // TODO, this could lead to an issue
      // eslint-disable-next-line require-atomic-updates
      persistentStorage.auth = response;
      logger.info('Token is refreshed. Releasing lock', response)();
      // eslint-disable-next-line require-atomic-updates
      tokenLock = null;
      resolve();
    } catch (error) {
      logger.error('Token is failed to refresh. Releasing lock', error)();
      // Don't clear persistentStorage.auth here — onAccessDenied needs the id_token
      // to perform a proper Zitadel end_session redirect.
      // eslint-disable-next-line require-atomic-updates
      tokenLock = null;
      resolve();
    }
  }
}

/** Creates a configured Api instance with automatic token refresh, language header, and 401 handling */
export function useApi(): Api {
  const {lang} = useLang();
  const {t} = useI18n();
  const logger = useLogger();
  const router = useRouter();
  const api = new Api(
    // incorrect types in Zitadel OpenID

    async(): Promise<ZitadelGetTokenResponse> => {
      if (persistentStorage.authExpired) {
        // prevent updating token multiple times in parallel
        await refreshToken(logger, api);
      }
      return persistentStorage.auth!;
    },
    () => lang.value,
    async(): Promise<void> => {
      if (!LOGOUT_ON_401 || !persistentStorage.auth) {
        return;
      }
      // Read before the probe below: one 401'd request per in-flight call reaches this handler,
      // and the token is gone by the time the losers of that race resume.
      const idToken = persistentStorage.auth.id_token;
      // A 401 has two meanings here and only one of them is an expired session: an identity
      // that authenticated with Google but has no Balm account yet 401s on every `/me` call by
      // design (IPD-2844). Tearing the session down in that case strands the user mid-sign-up —
      // it ends the Zitadel session too, so they have to authenticate with Google again — and
      // tells them their session expired when it did not. Ask which case this is first; the
      // probe carries the token but is not wired back to this handler, so it cannot recurse.
      //
      // Only worth asking where the question can have a second answer: without a Google provider
      // configured for this environment there are no unregistered identities, an expired token
      // cannot belong to one, and neither can a session that got far enough to load the user —
      // an identity with no Balm account has nothing to load. Each of those skips straight to the
      // logout below rather than paying a round trip (and a refresh attempt) on the way out.
      //
      // And only where the answer leads somewhere: this handler serves the admin app too, which
      // has no sign-up route to send an unregistered identity to, and no unregistered identities
      // either — admins are provisioned, not self-registered.
      const store = useUserStore();
      const hasSignUpPage = router
        .resolve(`/${lang.value}${pagesPath.public.signUp}`)
        .matched.some(record => Boolean(record.components));
      const mayBeUnregistered = hasSignUpPage
        && Boolean(ZITADEL.google_idp_id)
        && !persistentStorage.authExpired
        && !store.userData;
      const {registered} = mayBeUnregistered
        ? await api.signUp.getRegistrationStatus().catch(() => ({registered: true}))
        : {registered: true};
      if (!persistentStorage.auth) {
        // Another 401 from the same page got here first and already tore the session down.
        return;
      }
      if (!registered) {
        // Back to the start of sign-up, not into the shortened flow: the token stays, so
        // "Continue with Google" there re-enters registration without authenticating again.
        await router.replace(pagesPath.public.signUp);
        return;
      }
      persistentStorage.auth = null;
      Notify.create({
        type: 'warning',
        message: t('session_expired'),
      });
      await endLocalSession({
        idToken,
        // The landing page belongs to the app, not to this handler: sign-up for the patient app,
        // login for the admin one, which has no sign-up page to land on (IPD-3076).
        redirectPath: unauthenticatedRedirectOf(router),
        // The toast above does not survive the redirect — Zitadel's, or the reload when this
        // happens on the sign-up page itself — so the reason travels with it. The patient
        // sign-up page repeats it; the admin login page has nowhere to say it and ignores it.
        postLogoutState: AppErrorCode.SESSION_EXPIRED,
        api,
        userStore: store,
        router,
      });
    },
    logger,
    async(): Promise<ZitadelGetTokenResponse | null> => {
      await refreshToken(logger, api);
      return persistentStorage.auth;
    },
  );
  return api;
}
